/**
 * @fileoverview Custom hook for express enrollment box impression tracking.
 * Combines enrollment context with useBoxImpressionTracking for convenience.
 * Automatically provides all enrollment context so you don't have to fetch it in every component.
 */

import { useSelector } from "react-redux";
import { useBoxImpressionTracking } from "../../../../../utils/pendo/useBoxImpressionTracking";
import { FeatureGroups } from "../../../../../utils/pendo/useStandardizedTracking";
import { selectApplicationForm, selectCurrentApplication } from "../../../../../store/selector";
import { getCurrentStep } from "../utils/getCurrentStep";

/**
 * Custom hook for express enrollment box impression tracking.
 * Automatically provides enrollment context (potentialSaving, assistanceProgram, etc.)
 * so you don't have to fetch it in every component.
 *
 * @param {Object} options - Tracking options.
 * @param {string} options.contentKey - Content identifier.
 * @param {string} options.contentType - Content type (e.g., "step", "widget", "form").
 * @param {string} options.layout - Layout type (e.g., "step", "form", "widget").
 * @param {string} [options.feature] - Feature identifier (e.g., "ee_first_step", "docusign", "ee_third_step").
 * @param {Object} [options.additionalInfo] - Additional event-specific data.
 * @param {boolean} [options.enabled=true] - Whether tracking is enabled.
 * @param {number} [options.threshold=0.5] - Intersection threshold (0-1).
 * @param {number} [options.visibilityDuration=1500] - Duration in milliseconds before tracking (defaults to 1.5s for express enrollment).
 * @returns {React.RefObject<HTMLElement | null>} Ref to attach to the element to track.
 *
 * @example
 * ```jsx
 * const stepRef = useExpressEnrollmentBoxImpression({
 *   contentKey: "enrollment_step_completed",
 *   contentType: "step",
 *   layout: "step",
 *   feature: "ee_first_step",
 *   additionalInfo: {
 *     step_number: 1,
 *     is_completed: true
 *   }
 * });
 *
 * return <div ref={stepRef}>Step content</div>;
 * ```
 */
export const useExpressEnrollmentBoxImpression = ({
  contentKey,
  contentType,
  layout,
  feature,
  additionalInfo = {},
  enabled = true,
  threshold = 0.5,
  visibilityDuration = 1000
}) => {
  // Get enrollment context directly (same as useEnrollmentTracking does internally)
  const applicationForm = useSelector(selectApplicationForm);
  const application = useSelector(selectCurrentApplication);

  const potentialSaving = application?.potentialSaving;
  const templateName = potentialSaving?.templateName;
  const applicationFormId = applicationForm?.id;
  const assistanceProgram = potentialSaving?.assistanceProgram;
  const isExpressEnrollment = !!application?.hasExpressEnroll;
  const currentStep = getCurrentStep(applicationForm);
  const applicationFormStatus = applicationForm?.status;

  // Combine with useBoxImpressionTracking
  return useBoxImpressionTracking({
    featureGroup: FeatureGroups.EXPRESS_ENROLLMENT,
    pageKey: "enrollment_details",
    contentType,
    contentKey,
    layout,
    feature,
    potentialSaving,
    assistanceProgram,
    additionalInfo: {
      current_step: currentStep,
      supports_express_enrollment: isExpressEnrollment,
      template_name: templateName,
      application_form_id: applicationFormId,
      application_form_status: applicationFormStatus,
      ...additionalInfo
    },
    enabled,
    threshold,
    visibilityDuration
  });
};
